import { useEffect, useState } from 'react';

const CRITERIA = [
  { key: 'professionalism', label: 'Professionalism' },
  { key: 'ethicalCode', label: 'Ethical Code' },
  { key: 'timeRespect', label: 'Time Respect' },
  { key: 'communication', label: 'Communication' },
] as const;

export type EvalScores = {
  professionalism: number;
  ethicalCode: number;
  timeRespect: number;
  communication: number;
};

const empty: EvalScores = {
  professionalism: 0,
  ethicalCode: 0,
  timeRespect: 0,
  communication: 0,
};

function Star({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function ClinicRatingModal({
  studentName,
  open,
  onClose,
  onSubmit,
  submitting,
}: {
  studentName: string;
  weekLabel?: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (scores: EvalScores, comment?: string) => void;
  submitting: boolean;
}) {
  const [scores, setScores] = useState<EvalScores>(empty);
  const [comment, setComment] = useState('');
  const [hint, setHint] = useState('');

  useEffect(() => {
    if (open) {
      setScores(empty);
      setComment('');
      setHint('');
    }
  }, [open, studentName]);

  if (!open) return null;

  const allRated = CRITERIA.every(c => scores[c.key] > 0);

  return (
    <div
      className="cd-overlay open"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cd-modal cd-modal-lg" role="dialog" aria-modal="true" aria-labelledby="cd-rate-title">
        <div className="cd-modal-hd">
          <div>
            <div className="cd-modal-title" id="cd-rate-title">
              Rate Intern — {studentName}
            </div>
          </div>
          <button type="button" className="cd-modal-x" onClick={onClose} aria-label="Close">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cd-criteria">
          {CRITERIA.map(c => (
            <div className="cd-c-row" key={c.key}>
              <div className="cd-c-name">{c.label}</div>
              <div className="cd-stars">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`cd-star-btn${scores[c.key] >= n ? ' filled' : ''}`}
                    onClick={() => setScores(p => ({ ...p, [c.key]: n }))}
                    aria-label={`${c.label} ${n} stars`}
                  >
                    <Star filled={scores[c.key] >= n} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <label className="cd-c-name" style={{ display: 'block', marginBottom: 6 }}>
          Comment (optional)
        </label>
        <input
          className="cd-search-input"
          style={{ width: '100%', marginBottom: 14, paddingLeft: 10 }}
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Optional note…"
        />

        {hint && (
          <p style={{ fontSize: 12, color: 'var(--cd-orange)', marginBottom: 12 }}>{hint}</p>
        )}

        <hr className="cd-modal-hr" />
        <div className="cd-modal-ft">
          <div className="cd-week-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--cd-green)" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            Rating submits and marks the week complete
          </div>
          <button type="button" className="cd-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="cd-btn-submit"
            disabled={submitting}
            onClick={() => {
              if (!allRated) {
                setHint('Please rate all 4 criteria.');
                return;
              }
              onSubmit(scores, comment.trim() || undefined);
            }}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
